import React, { useMemo, useState } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import FilterButtonIcon from 'resources/WStore_Filters_Button.png';
import SapphireIcon from 'resources/icons-currency-sapphire-sml.png';
import WScoreIcon from 'resources/ui-icon-wscore.png';
import WStoreFilterMenu from '../WStoreFilterMenu';
import WStoreItem from '../WStoreItem';
import WStoreItemNew from '../WStoreItemNew';
import './WStorePagination.css';

const defaultDateFilter = {
  all: true,
  today: false,
  tomorrow: false,
  thisWeek: false,
  exactDate: false
};

const WStorePagination = ({
  onSelectItem,
  onSelectItemWithTrade,
  patronAssets,
  exchangeRates,
  showVaultBalance,
  wstoreItems,
  preferredLocation
}) => {
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [dateFilter, setDateFilter] = useState(defaultDateFilter);
  const [tempDateFilter, setTempDateFilter] = useState(dateFilter);
  const [items, setItems] = useState(wstoreItems);

  const WStoreItemComponent = showVaultBalance ? WStoreItemNew : WStoreItem;

  const today = moment().startOf('day');
  const tomorrow = moment().add(1, 'days');
  const thisWeekEnd = moment().add(7, 'days').endOf('day');

  const filteredItems = useMemo(() => {
    if (tempDateFilter.all) {
      return wstoreItems;
    }

    const isInThisWeek = (claimStart, claimEnd) => {
      const claimStartInThisWeek = claimStart.isBetween(
        today,
        thisWeekEnd,
        null,
        '[]'
      );
      const claimEndInThisWeek =
        claimEnd && claimEnd.isBetween(today, thisWeekEnd, null, '[]');
      const claimSpansThisWeek =
        claimStart.isSameOrBefore(today) &&
        claimEnd &&
        claimEnd.isSameOrAfter(thisWeekEnd);
      return claimStartInThisWeek || claimEndInThisWeek || claimSpansThisWeek;
    };

    const isAnyFilterActive = Object.keys(tempDateFilter).some(
      k => k !== 'exactDate' && tempDateFilter[k]
    );
    if (!isAnyFilterActive && tempDateFilter.exactDate === true) {
      return [];
    }

    return wstoreItems.filter(item => {
      const claimStart = item.claimStartDate
        ? moment(item.claimStartDate, 'MM/DD/YYYY')
        : null;
      const claimEnd = item.claimEndDate
        ? moment(item.claimEndDate, 'MM/DD/YYYY')
        : null;

      if (!claimStart) {
        return true;
      }

      const isInRange = date => {
        if (claimEnd) {
          return date.isBetween(claimStart, claimEnd, null, '[]');
        } else {
          return date.isSameOrAfter(claimStart, 'day');
        }
      };

      if (tempDateFilter.today && isInRange(today)) {
        return true;
      }

      if (tempDateFilter.tomorrow && isInRange(tomorrow)) {
        return true;
      }

      if (tempDateFilter.thisWeek && isInThisWeek(claimStart, claimEnd)) {
        return true;
      }

      if (
        moment.isMoment(tempDateFilter.exactDate) &&
        isInRange(tempDateFilter.exactDate)
      ) {
        return true;
      }

      return false;
    });
  }, [tempDateFilter, wstoreItems, today, tomorrow, thisWeekEnd]);

  const handleChangeTempFilter = key => {
    if (key === 'all') {
      if (!tempDateFilter.all) {
        setTempDateFilter(defaultDateFilter);
      }
      return;
    }

    let updatedFilters = {
      ...tempDateFilter,
      all: false,
      ...(moment.isMoment(key)
        ? {
            exactDate: key
          }
        : {
            [key]: !tempDateFilter[key]
          })
    };

    const isAnyFilterActive = Object.keys(updatedFilters).some(
      k => k !== 'all' && updatedFilters[k]
    );
    if (!isAnyFilterActive) {
      updatedFilters.all = true;
    }

    setTempDateFilter(updatedFilters);
  };

  const handleClearFilters = () => {
    setItems(wstoreItems);
    setShowFilterMenu(false);
    setDateFilter(defaultDateFilter);
    setTempDateFilter(defaultDateFilter);
  };

  const handleApplyFilters = () => {
    setItems(filteredItems);
    setShowFilterMenu(false);
    setDateFilter(tempDateFilter);
  };

  const handleCloseFilterMenu = () => {
    setShowFilterMenu(false);
    setTempDateFilter(dateFilter);
  };

  return (
    <>
      {showVaultBalance && (
        <div className="vault-balance-section">
          <div className="vault-balance-text">Vault Balance</div>
          <img src={SapphireIcon} alt="Sapphire" />
          <div className="sapphire-balance">{`Sapphires ${patronAssets.sapphires}`}</div>
          <img src={WScoreIcon} alt="WScore" />
          <div className="wscore-balance">{`WScore ${patronAssets.wscore}`}</div>
          <button
            className="filter-button"
            type="button"
            onClick={() => setShowFilterMenu(true)}
          >
            <img src={FilterButtonIcon} alt="Open Filter Menu" />
          </button>
          {showFilterMenu && (
            <WStoreFilterMenu
              showFilterMenu={showFilterMenu}
              onClose={handleCloseFilterMenu}
              tempDateFilter={tempDateFilter}
              filteredItemsCount={filteredItems?.length}
              handleClearFilters={handleClearFilters}
              handleChangeTempFilter={handleChangeTempFilter}
              handleApplyFilters={handleApplyFilters}
            />
          )}
        </div>
      )}
      {items.length > 0 && (
        <div className="wstore-list-content-container">
          <div className="wstore-list-content">
            {items.map((item, i) => (
              <div className="wstore-page-item" key={i}>
                <WStoreItemComponent
                  item={item}
                  patronAssets={patronAssets}
                  onSelect={onSelectItem}
                  onSelectItemWithTrade={onSelectItemWithTrade}
                  key={item.id}
                  exchangeRates={exchangeRates}
                  preferredLocation={preferredLocation}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

WStorePagination.propTypes = {
  patronAssets: PropTypes.any,
  wstoreItems: PropTypes.arrayOf(PropTypes.object),
  onSelectItem: PropTypes.func,
  onSelectItemWithTrade: PropTypes.func,
  preferredLocation: PropTypes.string,
  exchangeRates: PropTypes.shape({
    wscorePerNugget: PropTypes.number,
    wscorePerRuby: PropTypes.number,
    wscorePerSapphire: PropTypes.number,
    wscorePerWChip: PropTypes.number
  })
};

export default WStorePagination;
